import { useCallback, useMemo, useRef } from 'react'
import { useUnsavedChanges } from './useUnsavedChanges'

/**
 * Key order is stripped so an editor that rebuilds its object with a spread
 * doesn't read as a change, and undefined/null/'' are all treated as absent
 * because forms convert between the three constantly (a server null arrives in
 * an input as ''). Empty arrays and objects are kept — clearing a list is a
 * real edit.
 */
function normalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalize)
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(source).sort()) {
      const normalized = normalize(source[key])
      if (normalized === undefined || normalized === null || normalized === '') continue
      out[key] = normalized
    }
    return out
  }
  return value
}

export function serializeForCompare(value: unknown): string {
  return JSON.stringify(normalize(value)) ?? ''
}

export interface DirtyTracker {
  /** Capture the baseline, if it hasn't been captured yet. */
  touch: () => void
  /** Adopt the current values as the baseline. Call after a successful save. */
  markSaved: () => void
  /** Forget the baseline — the form is showing a different record now. */
  reset: () => void
  /** Whether the values differ from the baseline right now. */
  isDirty: () => boolean
  /** Spread onto the element wrapping the form to capture the baseline. */
  touchProps: {
    onPointerDownCapture: () => void
    onKeyDownCapture: () => void
  }
}

/**
 * Tracks whether a form's values have moved away from what the user found.
 *
 * The baseline is captured on the user's **first interaction**, not on mount.
 * Several metadata editors seed defaults into their values from an effect
 * (GoogleAdSlotEditor's ad sizes, VideoEmbedEditor's player id), so a
 * mount-time baseline would read as dirty before the user had done anything —
 * including on existing records that predate those fields. Capturing on the
 * first pointerdown/keydown folds that seeding into the baseline, and does the
 * same for any editor that grows a defaults effect later. Both events fire
 * before React commits the change they trigger, so the baseline is genuinely
 * the pre-edit state.
 *
 * A form the user never touches is never dirty, which is what we want: it has
 * nothing of theirs in it.
 */
export function useDirtyTracker(getValues: () => unknown): DirtyTracker {
  const getValuesRef = useRef(getValues)
  getValuesRef.current = getValues

  // null means "not captured yet".
  const baseline = useRef<string | null>(null)

  const touch = useCallback(() => {
    if (baseline.current === null) {
      baseline.current = serializeForCompare(getValuesRef.current())
    }
  }, [])

  const markSaved = useCallback(() => {
    baseline.current = serializeForCompare(getValuesRef.current())
  }, [])

  const reset = useCallback(() => {
    baseline.current = null
  }, [])

  const isDirty = useCallback(
    () =>
      baseline.current !== null &&
      serializeForCompare(getValuesRef.current()) !== baseline.current,
    [],
  )

  useUnsavedChanges(isDirty)

  // Every member is stable, so the tracker is too — callers can safely put it
  // in a dependency array.
  return useMemo(
    () => ({
      touch,
      markSaved,
      reset,
      isDirty,
      touchProps: { onPointerDownCapture: touch, onKeyDownCapture: touch },
    }),
    [touch, markSaved, reset, isDirty],
  )
}
